import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Tooltip,
  Modal,
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import dayjs from 'dayjs';
import RolePlusButton from 'assets/user-access/RolePlusButton.svg';
import RoleMinusButton from 'assets/user-access/RoleMinusButton.svg';
import dangerIcon from "assets/danger.svg";

const DynamicTable = ({
  headers,
  onRowsChange,
  userStartDates ,
  roleOptions,
  initialRows = [],
  userEndDate = null,
  setMessage,
  setSeverity,
  setOpenAlert,
  onValidateAllRows,
  historyMode = false, // Add new prop for history table styling
  onDeactivateRole = null, // New prop for role deactivation
  onRefreshData = null // New prop for refreshing data after deactivation
}) => {
  // Update the rows state to use initialRows
  const [rows, setRows] = useState(initialRows);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [serviceNumber, setServiceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const isEditMode = initialRows.length > 0;
  // Validate all rows function that can be called from parent
  const validateAllRows = () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!validateDates(row.roleStartDate, row.roleEndDate)) {
        setMessage(`Invalid dates in row ${i + 1}. Please check the dates.`);
        setSeverity("error");
        setOpenAlert(true);
        return false;
      }
    }
    return true;
  };
  // Expose the validation function to parent component
  useEffect(() => {
    if (onValidateAllRows) {
      onValidateAllRows(validateAllRows);
    }
  }, [rows]);

  // Add useEffect to update rows when initialRows changes
  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(initialRows);
      onRowsChange(initialRows);
    }
  }, [initialRows]);

  // Initial row data structure
  const initialRowData = {
    roleStartDate: null,
    roleEndDate: null,
    roleName: '',
    serviceNumber: '',
    remarks: '',
    status: 'Active', // Always static
    createdDate: dayjs().format('DD/MM/YYYY | HH:mm') // Current date/time
  };

  const addRow = () => {
    if (isEditMode) {
      // In edit mode, just add a new row
      setRows([...rows, { ...initialRowData, isNew: true }]);
      return;
    }
    if (!userEndDate) {
      setMessage("Please select user end date first");
      setSeverity("error");
      setOpenAlert(true);
      return;
    }

    // Get the last row
    const lastRow = rows[rows.length - 1];

    // First check if all fields are filled
    if (lastRow && !isRowComplete(lastRow)) {
      setMessage("Please fill all fields before adding a new row");
      setSeverity("error");
      setOpenAlert(true);
      return;
    }

    // Then validate the dates
    if (lastRow && !validateDates(lastRow.roleStartDate, lastRow.roleEndDate)) {
      return; // validateDates function already shows the error message
    }
    setRows([...rows, { ...initialRowData }]);
  };

  const removeRow = (index) => {
    const row = rows[index];
    // Show confirmation modal only for existing rows (from API) in edit mode
    if (isEditMode && row.userProfileLinkId && !row.isNew) {
      setSelectedRowIndex(index);
      setIsModalOpen(true);
    } else {
      // Directly remove new rows or rows in create mode
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
      onRowsChange(newRows);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedRowIndex(null);
    setServiceNumber("");
  };

  const handleDeactivateRole = async () => {
    if (!serviceNumber) {
      setMessage("Please enter service number");
      setSeverity("error");
      setOpenAlert(true);
      return;
    }

    const row = rows[selectedRowIndex];
    if (onDeactivateRole) {
      try {
        setLoading(true);
        await onDeactivateRole(row.userProfileLinkId, serviceNumber);
        setLoading(false);
        if (onRefreshData) {
          await onRefreshData();
          setRows(initialRows.filter((_, i) => i !== selectedRowIndex));
        }
        setMessage("Role deactivated successfully");
        setSeverity("success");
        setOpenAlert(true);
      } catch (error) {
        setLoading(false);

        setMessage(error.message || "Failed to deactivate role");
        setSeverity("error");
        setOpenAlert(true);
      }
    }
    handleModalClose();
  };

  const handleRowChange = (index, field, value) => {
    // For non-date fields or when clearing a date field
    if (field !== 'roleStartDate' && field !== 'roleEndDate' || !value) {
      const updatedRows = rows.map((row, i) => {
        if (i === index) {
          return { ...row, [field]: value };
        }
        return row;
      });
      setRows(updatedRows);
      onRowsChange(updatedRows);
      return;
    }

    // Function to update rows with the new value
    const updateRows = (newValue) => {
      const updatedRows = rows.map((row, i) => {
        if (i === index) {
          return { ...row, [field]: newValue };
        }
        return row;
      });
      setRows(updatedRows);
      onRowsChange(updatedRows);
    };

    // Function to show error without clearing date
    const showError = (message) => {
      setMessage(message);
      setSeverity("error");
      setOpenAlert(true);
    };
    const currentRow = rows[index];
    const userEndDateTime = dayjs(userEndDate).startOf('day');
    const userStartDate = userStartDates || dayjs().startOf('day');

    const selectedDateTime = dayjs(value).startOf('day');


    if (field === 'roleStartDate') {
      // Validation 1: Cannot be after or same as user end date
      if (selectedDateTime.isBefore(userStartDate) ) {
        showError(`User is onboarded in Agri360 on ${dayjs(userStartDate).format('DD/MM/YYYY')}. Please select a date after the same.`);
      }
      if (selectedDateTime.isAfter(userEndDate)) {
        showError(`User is onboarded in Agri360 on ${dayjs(userEndDate).format('DD/MM/YYYY')}. Please select a date before the same.`);
      }
      if ( selectedDateTime.isBefore(userStartDate)) {
        showError(`Role Start Date cannot be Past date, Please select current date or future date.`);
      }
    
      // Validation 5: Cannot be after or same as role end date
      if (currentRow.roleEndDate &&
        (selectedDateTime.isAfter(dayjs(currentRow.roleEndDate)))) {
        showError("Role Start Date Can not be same or after the Role End date.");
      }
    }

    if (field === 'roleEndDate') {
            // Validation 3: Cannot be after or same as user end date
      if(selectedDateTime.isBefore(userStartDate) ) {
        showError(`User is onboarded in Agri360 on ${dayjs(userStartDate).format('DD/MM/YYYY')}. Please select a date after the same.`);
      }

      if (selectedDateTime.isAfter(userEndDate)) {
        showError(`User End Date in Agri360 is ${dayjs(userEndDate).format('DD/MM/YYYY')}. Please select a date before the same.`);
      }

      // Validation 4: Cannot be before or same as user start date
      if (selectedDateTime.isBefore(userStartDate)) {
        showError("Role End Date cannot be Past date, please select future date.");
      }

      // Validation 6: Cannot be before or same as role start date
      if (currentRow.roleStartDate &&
        (selectedDateTime.isBefore(dayjs(currentRow.roleStartDate)) ||
          selectedDateTime.isSame(dayjs(currentRow.roleStartDate)))) {
        showError("Role End Date Can not be same or before the Role Start date");
      }
    }

    // If all validations pass, update with the new value
    updateRows(value);
  };

  const validateDates = (startDate, endDate) => {
    if (!startDate || !endDate) return true; // Let isRowComplete handle null checks

    const userStartDate = dayjs().startOf('day');
    const userEndDateTime = dayjs(userEndDate).startOf('day');
    const roleStartDateTime = dayjs(startDate).startOf('day');
    const roleEndDateTime = dayjs(endDate).startOf('day');

    // Role start date validations
    if (roleStartDateTime.isAfter(userEndDateTime) || roleStartDateTime.isSame(userEndDateTime)) {
      setMessage(`User End Date in Agri360 is ${dayjs(userEndDate).format('DD/MM/YYYY')}. Please select a date before the same.`);
      setSeverity("error");
      setOpenAlert(true);
      return false;
    }

    if (roleStartDateTime.isBefore(userStartDate) ) {
      setMessage("Role Start Date cannot be Past date, Please select current date or future date.");
      setSeverity("error");
      setOpenAlert(true);
      return false;
    }

    // Role end date validations
    if (roleEndDateTime.isAfter(userEndDateTime) || roleEndDateTime.isSame(userEndDateTime)) {
        setMessage(`User End Date in Agri360 is ${dayjs(userEndDate).format('DD/MM/YYYY')}. Please select a date before the same.`);
      setSeverity("error");
      setOpenAlert(true);
      return false;
    }

    if (roleEndDateTime.isBefore(userStartDate) || roleEndDateTime.isSame(userStartDate)) {
      setMessage("Role End Date cannot be Past date, please select future date.");
      setSeverity("error");
      setOpenAlert(true);
      return false;
    }

    // Compare role start and end dates
    if (roleStartDateTime.isAfter(roleEndDateTime) || roleStartDateTime.isSame(roleEndDateTime)) {
      setMessage("Role Start Date Can not be same or after the Role End date.");
      setSeverity("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  const isRowComplete = (row) => {
    return row.roleStartDate &&
      row.roleEndDate &&
      row.roleName
  };

  // Adjust grid columns based on history mode
  const gridTemplateColumns = historyMode ?
    'repeat(5, 1fr) 80px 180px' : // No action column in history mode
    '50px repeat(5, 1fr) 80px 180px'; // Action column for normal mode
  return (
    <Box sx={{ width: '100%' }}>
      {/* Headers with Action column */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: gridTemplateColumns, // Use the new template
        gap: 1,
        bgcolor: historyMode ? '#737D86' : '#0E2B63', // Use grey for history mode
        p: 1,
        borderRadius: '4px 4px 0 0',
        height: '52px', // Added fixed height
        alignItems: 'center' // Center content vertically
      }}>
        {/* Action column header - only show in normal mode */}
        {!historyMode && (
          <Typography sx={{
            fontWeight: 600,
            fontSize: '16px',
            color: '#ffffff' // Added for better contrast
          }}>
            {/* Action */}
          </Typography>
        )}
        {headers.map((header, index) => (
          <Typography
            key={index}
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: '#ffffff',
              whiteSpace: 'nowrap', // Prevent text wrapping
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {header}
          </Typography>
        ))}
      </Box>

      {/* Rows */}
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplateColumns, // Use the same template
            gap: 1,
            p: 1,
            borderBottom: '1px solid #e0e0e0',
            alignItems: 'center'
          }}
        >
          {/* Action icons - only show in normal mode */}
          {!historyMode && (
            <Box sx={{
              display: 'flex',
              gap: 0,
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Tooltip title={rowIndex === rows.length - 1 ? (!isRowComplete(row) ? "Fill all fields to add new row" : "Add new row") : ""}>
                <span>
                  <IconButton
                    size="small"
                    onClick={addRow}
                    disabled={rowIndex !== rows.length - 1 || !isRowComplete(row)}
                    sx={{
                      '&.Mui-disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <img src={RolePlusButton} alt="add" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => removeRow(rowIndex)}
              >
                <img src={RoleMinusButton} alt="remove" />
              </IconButton>
            </Box>
          )}

          {/* Render either input fields or static values */}
          {(!isEditMode || (isEditMode && row.isNew)) ? (
            // Input fields for the last row
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={row.roleStartDate}
                  onAccept={(newDate) => {
                    if (!newDate || !dayjs(newDate).isValid()) {
                      handleRowChange(rowIndex, 'roleStartDate', null);
                      return;
                    }
                    handleRowChange(rowIndex, 'roleStartDate', newDate);
                  }}
                  onChange={(newDate) => {
                    if (!newDate || !dayjs(newDate).isValid()) {
                      handleRowChange(rowIndex, 'roleStartDate', null);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Select Date",
                      error: false,
                      required: true,
                      label: "Select Date",
                      InputLabelProps: {
                        sx: {
                          color: '#000000',
                          paddingTop: '5px',
                          fontStyle: 'italic',
                          '&.Mui-focused': {
                            color: '#000000',
                            paddingTop: '0px',
                          }
                        }
                      },
                      InputProps: {
                        sx: {
                          height: '56px',
                          width: '100%'
                        }
                      }
                    }
                  }}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={row.roleEndDate}
                  onAccept={(newDate) => {
                    if (!newDate || !dayjs(newDate).isValid()) {
                      handleRowChange(rowIndex, 'roleEndDate', null);
                      return;
                    }
                    handleRowChange(rowIndex, 'roleEndDate', newDate);
                  }}
                  onChange={(newDate) => {
                    if (!newDate || !dayjs(newDate).isValid()) {
                      handleRowChange(rowIndex, 'roleEndDate', null);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "Select Date",
                      error: false,
                      required: true,
                      label: "Select Date",
                      InputLabelProps: {
                        sx: {
                          color: '#000000',
                          paddingTop: '5px',
                          fontStyle: 'italic',
                          '&.Mui-focused': {
                            color: '#000000',
                            paddingTop: '0px',

                          }
                        }
                      },
                      InputProps: {
                        sx: {
                          height: '56px',
                          width: '100%'
                        }
                      }
                    }
                  }}
                  format="DD/MM/YYYY"
                />

                {/* </Tooltip> */}
              </LocalizationProvider>
              <TextField
                select
                size="small"
                value={row.id}
                onChange={(e) => handleRowChange(rowIndex, 'roleName', e.target.value)}
                required
                label="Select"
                InputLabelProps={{
                  sx: {
                    color: '#000000',
                    paddingTop: '5px',
                    fontStyle: 'italic',
                    '&.Mui-focused': {
                      color: '#000000',
                      paddingTop: '0px',
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '56px',
                    width: '100%'
                  },
                  '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              >
                {roleOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                  >
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title={row.serviceNumber || ''}>
                <TextField
                  size="small"
                  value={row.serviceNumber}
                  onChange={(e) => handleRowChange(rowIndex, 'serviceNumber', e.target.value)}
                  placeholder="Type here"
                  required
                  label="Type here"
                  InputLabelProps={{
                    sx: {
                      color: '#000000',
                      paddingTop: '5px',
                      fontStyle: 'italic',
                      '&.Mui-focused': {
                        color: '#000000',
                        paddingTop: '0px',
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '56px',
                      width: '100%'
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title={row.remarks || ''}>
                <TextField
                  size="small"
                  value={row.remarks}
                  onChange={(e) => handleRowChange(rowIndex, 'remarks', e.target.value)}
                  placeholder="Type here"
                  label="Type here"
                  InputLabelProps={{
                    sx: {
                      color: '#000000',
                      paddingTop: '5px',
                      fontStyle: 'italic',
                      '&.Mui-focused': {
                        color: '#000000',
                        paddingTop: '0px',
                      }
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '56px',
                      width: '100%'
                    },
                    '& .MuiInputBase-input': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              </Tooltip>
              {/* Static Status field */}
              <Typography sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                padding: '0 8px',
                cursor: 'default',
                textAlign: 'left' // Added to match header alignment
              }}>
                Active
              </Typography>
              {/* Static Created Date field */}
              <Typography>{row.createdDate}</Typography>
            </>
          ) : (
            // Static values for completed rows
            <>
              <Tooltip title={dayjs(row.roleStartDate).format('DD/MM/YYYY')}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {dayjs(row.roleStartDate).format('DD/MM/YYYY')}
                </Typography>
              </Tooltip>
              <Tooltip title={dayjs(row.roleEndDate).format('DD/MM/YYYY')}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {dayjs(row.roleEndDate).format('DD/MM/YYYY')}
                </Typography>
              </Tooltip>
              <Tooltip title={row.roleName}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {row.roleName}
                </Typography>
              </Tooltip>
              <Tooltip title={row.serviceNumber}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {row.serviceNumber}
                </Typography>
              </Tooltip>
              <Tooltip title={row.remarks}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {row.remarks}
                </Typography>
              </Tooltip>
              <Tooltip title={row.status}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {row.status}
                </Typography>
              </Tooltip>
              <Tooltip title={row.createdDate}>
                <Typography sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  padding: '0 8px',
                  cursor: 'default',
                  textAlign: 'left'
                }}>
                  {row.createdDate}
                </Typography>
              </Tooltip>
            </>
          )}
        </Box>
      ))}

      {/* Add Row Button - shown only when no rows exist */}
      {!isEditMode && rows.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            onClick={addRow}
            sx={{
              backgroundColor: '#0A3575',
              '&:hover': {
                backgroundColor: '#0A3575',
              },
              textTransform: 'none', // To keep the text case as provided
              fontWeight: 600,
              height: '40px',
            }}
          >
            ADD NEW
          </Button>
        </Box>
      )}

      {/* Confirmation Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: { xs: "12rem", md: "525px" },
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
          backgroundColor: 'white',
          borderRadius: "12px"
        }}>
          <img src={dangerIcon} />
          <Typography
            sx={{
              fontSize: "16px",
              color: "#000000",
              textAlign: "center",
              marginBottom: "20px",
              fontStyle: "Montserrat",
              fontWeight: 500,
            }}
            variant="h8"
          >
            Do you really want to inactivate the Role.<br />
            If Yes, Please enter the SR Number, Or click No
          </Typography>
          <TextField
            // label="Service Number"

            fullWidth
            multiline
            rows={2}
            value={serviceNumber}
            onChange={(e) => setServiceNumber(e.target.value)}
            placeholder="SR Number"
            InputProps={{
              endAdornment: serviceNumber ? null : (
                <span style={{
                  color: 'red',
                  position: 'absolute',
                  left: '110px',
                  top: '15px',
                  fontSize: '16px'
                }}>*</span>
              )
            }}
            sx={{
              boxShadow: '0px 2px 4px 0px #ECEBEB80',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#E0E0E0',
                },
                '&:hover fieldset': {
                  borderColor: '#E0E0E0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#E0E0E0',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: 'Montserrat',
                fontStyle: 'italic',
                fontSize: '16px',
                fontWeight: 400,
                '&::placeholder': {
                  color: '#A7A9B0',
                  opacity: 1,
                  fontFamily: 'Montserrat',
                  fontStyle: 'italic',
                  fontSize: '16px',
                  fontWeight: 400,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #E0E0E0',
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
            <Button
              sx={{
                borderRadius: 4,
                minWidth: 90,
                minHeight: 42,
                background: "#737D86",
                "&:hover": {
                  backgroundColor: "#737D86",
                },
              }}
              variant="contained"
              type="button"
              onClick={handleModalClose}
            >
              NO
            </Button>
            <Button
              sx={{
                borderRadius: 4,
                minWidth: 90,
                minHeight: 42,
                background: "#EF7D00",
                "&:hover": {
                  backgroundColor: "#EF7D00",
                },
                "&:disabled": {
                  backgroundColor: "#EF7D00",
                  opacity: 0.8,
                },
              }}
              variant="contained"
              type="button"
              disabled={!serviceNumber.trim() || loading}
              onClick={handleDeactivateRole}
            >
              YES
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DynamicTable;